import { createFileRoute } from "@tanstack/react-router";
import { Check, ClipboardCheck, Sparkles, X, ShieldAlert, Award, FileSpreadsheet } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/why-aureline")({
  component: WhyAureline,
  head: () => ({
    meta: [
      { title: "Why Aureline — Verified Compliance & Quality Inspections" },
      { name: "description", content: "Discover the Aureline Difference. See how our checklist-driven clean certified reporting, supervised inspections, and verified accountability compares to standard janitorial services." },
    ],
  }),
});

const featuresComparison = [
  {
    name: "Visit Verification",
    standard: "No proof of attendance; hours logged manually or self-reported.",
    aureline: "GPS-stamped, time-logged logins through our operations portal.",
    highlight: true
  },
  {
    name: "Checklist Accountability",
    standard: "Paper checklists left in a binder, often checked off all at once.",
    aureline: "Digital site checklists required to be completed on-shift.",
    highlight: false
  },
  {
    name: "Clean Certified Proof",
    standard: "Rely on walking the building yourself to spot issues.",
    aureline: "Clean certified verification of key zones loaded onto reports.",
    highlight: true
  },
  {
    name: "Quality Inspections",
    standard: "Infrequent walkthroughs or reactive audits after a complaint.",
    aureline: "Weekly supervisor audits with graded reports.",
    highlight: true
  },
  {
    name: "Communication Loop",
    standard: "Calling/emailing off-site managers and waiting days for response.",
    aureline: "Direct portal logs and responsive local operations hubs.",
    highlight: false
  }
];

function WhyAureline() {
  return (
    <Layout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-secondary/40 via-secondary/10 to-background py-20 border-b border-border/40">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-navy eyebrow animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-sky" /> The Aureline Standard
            </div>
            <h1 className="mt-6 font-display text-4xl md:text-6xl text-navy leading-[1.2] tracking-tight animate-fade-up delay-100">
              Accountability you can <br />
              <span className="italic text-navy-soft">verify</span>. Proof in every visit.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-up delay-200">
              We eliminate the guesswork. Through our clean certified checklists and supervised quality reporting, 
              you receive clear, verifiable data that the job was done correctly.
            </p>
          </div>
        </div>
      </section>

      {/* INSPECTION DEEP DIVE */}
      <section className="py-24">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="eyebrow text-navy-soft">Quality Assurance</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy leading-tight tracking-tight">
                Clean Certified.
              </h2>
              <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
                Aureline properties are backed by a standardized inspection cycle. 
                Instead of hoping your cleaning provider followed through, you receive a graded, 
                checklist-driven inspection report compiled by our local site supervisors.
              </p>
              
              <div className="mt-10 space-y-6">
                <div className="flex gap-4 animate-fade-up">
                  <span className="w-10 h-10 rounded-full bg-mint/35 flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-mint-ink" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-navy font-semibold">100% Itemized Audits</h3>
                    <p className="text-sm text-muted-foreground mt-1">We inspect surfaces, waste systems, common corridors, and amenities according to a strict, site-specific protocol.</p>
                  </div>
                </div>

                <div className="flex gap-4 animate-fade-up delay-100">
                  <span className="w-10 h-10 rounded-full bg-sky/30 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-navy-soft" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-navy font-semibold">Graded Metrics</h3>
                    <p className="text-sm text-muted-foreground mt-1">Every inspection receives a clear score. This data lets us address quality drift proactively before it becomes a problem.</p>
                  </div>
                </div>

                <div className="flex gap-4 animate-fade-up delay-200">
                  <span className="w-10 h-10 rounded-full bg-ice flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="w-5 h-5 text-navy-deep" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-navy font-semibold">Client-Facing Visibility</h3>
                    <p className="text-sm text-muted-foreground mt-1">Reports are delivered directly to your email or portal, providing standard records for strata councils, tenants, and boards.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="gradient-navy-blue text-primary-foreground p-8 xl:p-12 rounded-3xl shadow-elevated relative overflow-hidden animate-scale-in">
              <div className="absolute top-0 right-0 w-24 h-24 stripe-accent-fade" aria-hidden />
              <div className="eyebrow text-mint">Sample Inspection Report</div>
              <div className="mt-6 border-b border-primary-foreground/15 pb-4">
                <div className="flex justify-between items-center">
                  <span className="font-display text-2xl">Aureline Facility Audit</span>
                  <span className="bg-mint text-mint-ink px-3 py-1 rounded-full text-xs font-semibold">SCORE: 98%</span>
                </div>
                <div className="text-xs text-primary-foreground/75 mt-2">Date: June 2, 2026 · Inspector: S. Supervisor</div>
              </div>
              <ul className="mt-6 space-y-4 text-sm">
                {[
                  { label: "Main lobby touchpoints disinfected", verified: true },
                  { label: "Elevator mirrors & tracks polished", verified: true },
                  { label: "Restroom supply levels replenished", verified: true },
                  { label: "Amenity corridor carpet steam-cleaned", verified: true },
                  { label: "Rear stairwell waste bins emptied", verified: true },
                  { label: "High-level glass sill dust check", verified: true }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center justify-between">
                    <span className="text-primary-foreground/90">{item.label}</span>
                    <span className="flex items-center gap-1 text-xs font-semibold text-mint">
                      <Check className="w-4 h-4 text-mint" /> VERIFIED
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-primary-foreground/15 flex justify-between items-center">
                <span className="text-xs text-primary-foreground/60">Inspected via Aureline Operations Portal</span>
                <span className="text-xs text-sky font-semibold">Clean Certified Verification Status Logged</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 bg-cream">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="eyebrow text-navy-soft">Side-by-Side</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy tracking-tight">
              Standard Cleaning vs. Aureline.
            </h2>
            <p className="mt-4 text-muted-foreground xl:text-lg">
              Compare the visibility and execution structures that set Aureline apart.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border/60 shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="gradient-navy-blue text-primary-foreground">
                    <th className="p-6 font-display text-lg">Feature</th>
                    <th className="p-6 font-display text-lg">Standard Janitorial</th>
                    <th className="p-6 font-display text-lg">Aureline Service</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {featuresComparison.map((f, index) => (
                    <tr key={index} className={f.highlight ? "bg-secondary/15" : "bg-transparent"}>
                      <td className="p-6 font-display text-base text-navy font-semibold">{f.name}</td>
                      <td className="p-6 text-sm text-muted-foreground leading-relaxed flex items-start gap-2.5">
                        <X className="w-4 h-4 text-destructive flex-shrink-0 mt-1" />
                        <span>{f.standard}</span>
                      </td>
                      <td className="p-6 text-sm text-navy leading-relaxed font-medium">
                        <div className="flex items-start gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-mint/55 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-mint-ink font-bold" />
                          </span>
                          <span>{f.aureline}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-24">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <Sparkles className="w-12 h-12 text-mint mx-auto" />
            <h2 className="mt-6 font-display text-4xl md:text-5xl text-navy tracking-tight">
              Experience verified cleaning today.
            </h2>
            <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
              Strata councils, retail managers, and commercial portfolios across BC choose 
              Aureline because they want confidence in their service provider. Get a transparent, 
              practical proposal customized for your property.
            </p>
            <div className="mt-10 flex justify-center">
              <Link to="/" hash="contact">
                <Button className="rounded-full h-12 px-8 bg-navy text-primary-foreground hover:bg-navy/90 text-base">
                  Request a quote <Check className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
